import { createApi } from '@reduxjs/toolkit/dist/query/react'
import baseQueryWithOnlineCourse from '../../app/baseQueryWithOnlineCourse'
import { CourseAnalyticState, CourseState, ListResponse, PaginationState, UserCourseState } from '../types'

interface PromocodeParams {
    promocode: string
}

export const courseApiSlice = createApi({
    reducerPath: 'courseApi',
    baseQuery: baseQueryWithOnlineCourse,
    tagTypes: ['Course', 'UserCourse', 'Analytic'],
    endpoints: (build) => ({
        fetchAllCourses: build.query<CourseState[], void>({
            query: () => ({
                url: '/course',
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ id }) => ({
                              type: 'Course' as const,
                              id,
                          })),
                          { type: 'Course', id: 'LIST' },
                      ]
                    : [{ type: 'Course', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        getCourseById: build.query<CourseState, number>({
            query: (courseId: number) => ({
                url: `course/${courseId}`,
            }),
            transformResponse: (response: any) => response.data,
        }),

        createCourse: build.mutation<void, FormData>({
            query: (course) => ({
                url: '/course',
                method: 'POST',
                body: course,
            }),
            invalidatesTags: [{ type: 'Course', id: 'LIST' }],
        }),

        updateCourse: build.mutation<void, FormData>({
            query: (course) => ({
                url: `/course/${course.get('id')}`,
                method: 'PUT',
                body: course,
            }),
            invalidatesTags: [{ type: 'Course', id: 'LIST' }],
        }),

        deleteCourse: build.mutation<{ success: boolean; id: number }, number>({
            query: (id) => ({
                url: `/course/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, id) => [{ type: 'Course', id }],
        }),

        duplicateCourse: build.mutation<void, number>({
            query: (id) => ({
                url: `/course/duplicate/${id}`,
                method: 'POST',
            }),
            invalidatesTags: [{ type: 'Course', id: 'LIST' }],
        }),

        fetchAllUserCourses: build.query<UserCourseState[], void>({
            query: () => ({
                url: `user-course/my-courses`,
            }),
            providesTags: (result) =>
                result
                    ? [
                          ...result.map(({ courseId }) => ({
                              type: 'Course' as const,
                              courseId,
                          })),
                          { type: 'Course', id: 'LIST' },
                      ]
                    : [{ type: 'Course', id: 'LIST' }],
            transformResponse: (response: any) => response.data,
        }),

        enrollToCourseByPromocode: build.mutation<void, PromocodeParams>({
            query: (body) => ({
                url: 'purchase/by-promocode',
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'Course', id: 'LIST' }],
        }),

        fetchAnalytics: build.query<
            ListResponse<CourseAnalyticState>,
            PaginationState
        >({
            query: ({ page, size }) => ({
                url: `analytics/course`,
                params: {
                    page,
                    size,
                },
            }),
            providesTags: (result) =>
                result
                    ? [
                            ...result.data.map(({ id }) => ({
                                type: 'Analytic' as const,
                                id,
                            })),
                            { type: 'Analytic', id: 'LIST' },
                        ]
                    : [{ type: 'Analytic', id: 'LIST' }],
            transformResponse: (response: {
                data: ListResponse<CourseAnalyticState>
            }) => ({
                data: response?.data?.data,
                pageNumber: response?.data?.pageNumber,
                totalCount: response?.data?.totalCount,
                totalPages: response?.data?.totalPages,
            }),
        }),
    }),
})
